/**
 * Self-test suite: Stories (ephemeral encrypted posts)
 *
 * Mirrors iOS `StorySelfTest.swift`. Five groups:
 *
 *   A. Key + cipher — pure crypto, AAD binding, TTL gate
 *      A1. StoryKey.generate returns 32 bytes
 *      A2. seal + open round-trips a small blob
 *      A3. flipping a byte in the ciphertext fails open with openFailure
 *      A4. open at createdAt + 25h yields expired (TTL is client-enforced)
 *      A5. open at createdAt + 23h still succeeds (24h boundary inclusive)
 *      A6. AAD binding: open with mismatched metadata.storyId fails openFailure
 *
 *   B. Distribution codec — wire format + validation
 *      B1. encode + decode round-trips
 *      B2. isStoryDistribution true for our prefix, false for sibling codecs
 *      B3. 31-byte storyKey is rejected with invalidKeyLength
 *      B4. unsupported schema is rejected
 *
 *   C. Reaction codec — emoji discipline
 *      C1. encode + decode round-trips emoji "😀"
 *      C2. empty emoji (revoke) round-trips
 *      C3. multi-scalar ZWJ emoji is rejected on encode
 *      C4. unsupported schema is rejected on decode
 *
 *   D. Store — persistence, expiry, reactions
 *      D1. acceptDistribution stores the story (idempotent)
 *      D2. story(id, at) returns the story before expiry
 *      D3. story(id, at) returns null and auto-purges when expired
 *      D4. purgeExpired bulk: 3 expired + 2 active → 3 deleted, 2 retained
 *      D5. reactions LWW by ts (stale ts is dropped)
 *      D6. reactions sorted by reactingUserId
 *
 *   E. Hardening — server-substitution attack, revoke, per-story isolation
 *      E1. server-substitute attack: same encryptedBlob with different
 *          metadata.storyId fails open with openFailure (AAD mismatch)
 *      E2. revoke (empty emoji) removes a prior reaction
 *      E3. per-story isolation: two stories share a store but don't bleed
 *
 * All IndexedDB cases use isolated uniquely-named databases so the suite is
 * safe on every dev launch and cannot pollute the production database.
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  StoryKey,
  StoryCipher,
  makeStoryMetadata,
  STORY_TTL_SECONDS,
} from "./Story";
import type { StoryMetadata } from "./Story";
import {
  STORY_DISTRIBUTION_PREFIX,
  encodeStoryDistribution,
  decodeStoryDistribution,
  isStoryDistribution,
  tryDecodeStoryDistribution,
} from "./StoryKeyDistribution";
import type { StoryKeyDistributionPayload } from "./StoryKeyDistribution";
import {
  STORY_REACTION_PREFIX,
  encodeStoryReaction,
  decodeStoryReaction,
  isStoryReaction,
} from "./StoryReaction";
import type { StoryReactionPayload } from "./StoryReaction";
import { StoryStore } from "./StoryStore";

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Stories (ephemeral encrypted posts)",
  isVerified: true,
  run: async () =>
    buildSuiteResult([
      // Group A — key + cipher
      await caseGenerateReturns32Bytes(),
      await caseSealOpenRoundTrip(),
      await caseTamperedCiphertextRejected(),
      await caseExpiredOpenRejected(),
      await caseWithin24hStillOpenable(),
      await caseAADBindingMetadataMismatch(),
      // Group B — distribution codec
      await caseDistributionRoundTrip(),
      await caseDistributionPrefixDistinct(),
      await caseDistribution31ByteKeyRejected(),
      await caseDistributionUnsupportedSchemaRejected(),
      // Group C — reaction codec
      await caseReactionRoundTrip(),
      await caseReactionRevokeRoundTrip(),
      await caseReactionMultiScalarRejected(),
      await caseReactionUnsupportedSchemaRejected(),
      // Group D — store
      await caseStoreAcceptsIdempotent(),
      await caseStoreReturnsBeforeExpiry(),
      await caseStoreExpiryAutoPurges(),
      await caseStorePurgeExpiredBulk(),
      await caseStoreReactionsLWW(),
      await caseStoreReactionsSorted(),
      // Group E — hardening
      await caseServerSubstituteAttack(),
      await caseStoreRevokeReaction(),
      await casePerStoryIsolation(),
    ]),
});

// ---------------------------------------------------------------------------
// Group A: key + cipher
// ---------------------------------------------------------------------------

async function caseGenerateReturns32Bytes(): Promise<TestCase> {
  const k = StoryKey.generate();
  const ok = k.raw.length === 32;
  return ok
    ? pass("A1: StoryKey.generate returns 32 bytes")
    : fail("A1: StoryKey.generate returns 32 bytes", `got ${k.raw.length} bytes`);
}

async function caseSealOpenRoundTrip(): Promise<TestCase> {
  const name = "A2: seal + open round-trips a small blob";
  try {
    const key = StoryKey.generate();
    const blob = new TextEncoder().encode("hello-story");
    const meta = sampleMetadata();
    const sealed = await StoryCipher.seal(blob, meta, key);
    const opened = await StoryCipher.open(sealed, meta, key, meta.createdAt);
    const ok = opened.length === blob.length && opened.every((b, i) => b === blob[i]);
    return ok ? pass(name) : fail(name, `got ${opened.length} bytes, want ${blob.length}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function caseTamperedCiphertextRejected(): Promise<TestCase> {
  const name = "A3: tampered ciphertext fails open with openFailure";
  try {
    const key = StoryKey.generate();
    const blob = new TextEncoder().encode("hello-story");
    const meta = sampleMetadata();
    const sealed = await StoryCipher.seal(blob, meta, key);
    // Flip a byte in the middle (past the nonce, into the ciphertext).
    const tampered = sealed.slice();
    const mid = Math.floor(tampered.length / 2);
    tampered[mid] ^= 0x01;
    try {
      await StoryCipher.open(tampered, meta, key, meta.createdAt);
      return fail(name, "open succeeded on tampered blob");
    } catch (e) {
      if (isStoryErrorKind(e, "openFailure")) return pass(name);
      return fail(name, `expected openFailure, got ${String(e)}`);
    }
  } catch (e) {
    return fail(name, `seal failed: ${String(e)}`);
  }
}

async function caseExpiredOpenRejected(): Promise<TestCase> {
  const name = "A4: open at createdAt + 25h yields expired (TTL client-enforced)";
  const createdAt = 1_700_000_000;
  const meta = makeStoryMetadata("s1", "alice", 5, "image/jpeg", createdAt);
  try {
    const key = StoryKey.generate();
    const blob = new TextEncoder().encode("hello");
    const sealed = await StoryCipher.seal(blob, meta, key);
    const at25h = createdAt + 25 * 3600;
    try {
      await StoryCipher.open(sealed, meta, key, at25h);
      return fail(name, "open succeeded past 24h boundary");
    } catch (e) {
      if (isStoryErrorKind(e, "expired")) return pass(name);
      return fail(name, `expected expired, got ${String(e)}`);
    }
  } catch (e) {
    return fail(name, `seal failed: ${String(e)}`);
  }
}

async function caseWithin24hStillOpenable(): Promise<TestCase> {
  const name = "A5: open at createdAt + 23h still succeeds (within 24h TTL)";
  const createdAt = 1_700_000_000;
  const meta = makeStoryMetadata("s1", "alice", 5, "image/jpeg", createdAt);
  try {
    const key = StoryKey.generate();
    const blob = new TextEncoder().encode("hello");
    const sealed = await StoryCipher.seal(blob, meta, key);
    const at23h = createdAt + 23 * 3600;
    const opened = await StoryCipher.open(sealed, meta, key, at23h);
    const ok = opened.every((b, i) => b === blob[i]);
    return ok ? pass(name) : fail(name, "blob mismatch after open within 24h");
  } catch (e) {
    return fail(name, `unexpected error: ${String(e)}`);
  }
}

async function caseAADBindingMetadataMismatch(): Promise<TestCase> {
  const name = "A6: AAD binding — mismatched metadata.storyId fails openFailure";
  const metaA = sampleMetadata("story-A");
  const metaB = sampleMetadata("story-B");
  try {
    const key = StoryKey.generate();
    const blob = new TextEncoder().encode("hello");
    const sealed = await StoryCipher.seal(blob, metaA, key);
    try {
      await StoryCipher.open(sealed, metaB, key, metaA.createdAt);
      return fail(name, "open succeeded with different metadata.storyId");
    } catch (e) {
      if (isStoryErrorKind(e, "openFailure")) return pass(name);
      return fail(name, `expected openFailure, got ${String(e)}`);
    }
  } catch (e) {
    return fail(name, `seal failed: ${String(e)}`);
  }
}

// ---------------------------------------------------------------------------
// Group B: distribution codec
// ---------------------------------------------------------------------------

async function caseDistributionRoundTrip(): Promise<TestCase> {
  const name = "B1: distribution codec round-trips";
  try {
    const key = StoryKey.generate();
    const meta = sampleMetadata("story-1");
    const payload = makeDistributionPayload("story-1", key, meta);
    const encoded = encodeStoryDistribution(payload);
    const decoded = decodeStoryDistribution(encoded);
    const ok =
      decoded.storyId === payload.storyId &&
      decoded.storyKey === payload.storyKey &&
      decoded.metadata.storyId === meta.storyId &&
      decoded.schema === 1;
    return ok ? pass(name) : fail(name, `decoded mismatch: ${JSON.stringify(decoded)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function caseDistributionPrefixDistinct(): Promise<TestCase> {
  const name = "B2: isStoryDistribution distinguishes from sibling prefixes";
  const key = StoryKey.generate();
  const meta = sampleMetadata();
  const dist = encodeStoryDistribution(makeDistributionPayload(meta.storyId, key, meta));
  const ok =
    isStoryDistribution(dist) &&
    !isStoryDistribution("kaether:story:react:v1:foo") &&
    !isStoryDistribution("kaether:e2ee:v1:foo") &&
    !isStoryDistribution("kaether:receipt:v1:abc") &&
    !isStoryDistribution("kaether:tombstone:v1:abc") &&
    !isStoryDistribution("kaether:profile:share:v1:abc") &&
    !isStoryDistribution("just a chat message");
  return ok ? pass(name) : fail(name, "prefix collision detected");
}

async function caseDistribution31ByteKeyRejected(): Promise<TestCase> {
  const name = "B3: 31-byte storyKey is rejected with invalidKeyLength";
  // Hand-craft a payload with a 31-byte key, bypassing the encode precondition.
  const meta = sampleMetadata();
  const badKey = btoa(String.fromCharCode(...new Uint8Array(31)));
  const rawJson = JSON.stringify({
    schema: 1,
    storyId: meta.storyId,
    storyKey: badKey,
    metadata: meta,
  });
  const encoded =
    STORY_DISTRIBUTION_PREFIX +
    btoa(rawJson).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  try {
    decodeStoryDistribution(encoded);
    return fail(name, "decode accepted a non-32-byte key");
  } catch (e) {
    if (isStoryErrorKind(e, "invalidKeyLength")) return pass(name);
    return fail(name, `expected invalidKeyLength, got ${String(e)}`);
  }
}

async function caseDistributionUnsupportedSchemaRejected(): Promise<TestCase> {
  const name = "B4: distribution unsupported schema is rejected";
  const meta = sampleMetadata();
  const key32 = btoa(String.fromCharCode(...new Uint8Array(32)));
  const rawJson = JSON.stringify({
    schema: 99,
    storyId: meta.storyId,
    storyKey: key32,
    metadata: meta,
  });
  const encoded =
    STORY_DISTRIBUTION_PREFIX +
    btoa(rawJson).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  try {
    decodeStoryDistribution(encoded);
    return fail(name, "decode accepted schema=99");
  } catch (e) {
    if (isStoryErrorKind(e, "unsupportedSchema")) return pass(name);
    return fail(name, `expected unsupportedSchema, got ${String(e)}`);
  }
}

// ---------------------------------------------------------------------------
// Group C: reaction codec
// ---------------------------------------------------------------------------

async function caseReactionRoundTrip(): Promise<TestCase> {
  const name = "C1: reaction codec round-trips emoji \u{1F600}";
  try {
    const p: StoryReactionPayload = {
      schema: 1,
      storyId: "s1",
      reactingUserId: "bob",
      emoji: "\u{1F600}",
      ts: 1_700_000_000,
    };
    const encoded = encodeStoryReaction(p);
    const decoded = decodeStoryReaction(encoded);
    const ok =
      decoded.storyId === p.storyId &&
      decoded.reactingUserId === p.reactingUserId &&
      decoded.emoji === p.emoji &&
      decoded.ts === p.ts;
    return ok ? pass(name) : fail(name, `decoded: ${JSON.stringify(decoded)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function caseReactionRevokeRoundTrip(): Promise<TestCase> {
  const name = "C2: reaction codec round-trips revoke (empty emoji)";
  try {
    const p: StoryReactionPayload = {
      schema: 1,
      storyId: "s1",
      reactingUserId: "bob",
      emoji: "",
      ts: 1_700_000_000,
    };
    const encoded = encodeStoryReaction(p);
    const decoded = decodeStoryReaction(encoded);
    const ok = decoded.emoji === "" && decoded.storyId === "s1";
    return ok ? pass(name) : fail(name, `got: ${JSON.stringify(decoded)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function caseReactionMultiScalarRejected(): Promise<TestCase> {
  // ZWJ family: U+1F468 U+200D U+1F469 U+200D U+1F467 — multiple scalars.
  const name = "C3: multi-scalar ZWJ emoji is rejected on encode";
  const multi = "\u{1F468}\u{200D}\u{1F469}\u{200D}\u{1F467}";
  const p: StoryReactionPayload = {
    schema: 1,
    storyId: "s1",
    reactingUserId: "bob",
    emoji: multi,
    ts: 1_700_000_000,
  };
  try {
    encodeStoryReaction(p);
    return fail(name, "encode accepted multi-scalar emoji");
  } catch (e) {
    if (isStoryErrorKind(e, "malformed")) return pass(name);
    return fail(name, `expected malformed, got ${String(e)}`);
  }
}

async function caseReactionUnsupportedSchemaRejected(): Promise<TestCase> {
  const name = "C4: reaction unsupported schema is rejected on decode";
  const rawJson = JSON.stringify({
    schema: 99,
    storyId: "s1",
    reactingUserId: "bob",
    emoji: "\u{1F600}",
    ts: 1,
  });
  // btoa() only accepts Latin1; the emoji \u{1F600} would throw. Encode to
  // UTF-8 bytes first, then to a Latin1 byte-string, then to base64.
  const jsonBytes = new TextEncoder().encode(rawJson);
  const binStr = String.fromCharCode(...jsonBytes);
  const encoded =
    STORY_REACTION_PREFIX +
    btoa(binStr).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  try {
    decodeStoryReaction(encoded);
    return fail(name, "decode accepted schema=99");
  } catch (e) {
    if (isStoryErrorKind(e, "unsupportedSchema")) return pass(name);
    return fail(name, `expected unsupportedSchema, got ${String(e)}`);
  }
}

// ---------------------------------------------------------------------------
// Group D: store
// ---------------------------------------------------------------------------

async function caseStoreAcceptsIdempotent(): Promise<TestCase> {
  const name = "D1: acceptDistribution stores once (idempotent)";
  const s = freshStore();
  const { payload, blobB64 } = sampleAcceptInputs();
  const first = await s.acceptDistribution(
    payload,
    blobB64,
    payload.metadata.createdAt,
  );
  const second = await s.acceptDistribution(
    payload,
    blobB64,
    payload.metadata.createdAt,
  );
  const ok = first === true && second === false;
  return ok
    ? pass(name)
    : fail(name, `first=${first} second=${second}`);
}

async function caseStoreReturnsBeforeExpiry(): Promise<TestCase> {
  const name = "D2: story(id, at) returns the story before expiry";
  const s = freshStore();
  const { payload, blobB64 } = sampleAcceptInputs();
  await s.acceptDistribution(payload, blobB64, payload.metadata.createdAt);
  const fetched = await s.story(payload.storyId, payload.metadata.createdAt);
  const ok = fetched !== null && fetched.metadata.storyId === payload.storyId;
  return ok ? pass(name) : fail(name, `fetched=${JSON.stringify(fetched)}`);
}

async function caseStoreExpiryAutoPurges(): Promise<TestCase> {
  const name = "D3: story(id, at) auto-purges expired stories at read time";
  const s = freshStore();
  const { payload, blobB64 } = sampleAcceptInputs();
  const createdAt = payload.metadata.createdAt;
  await s.acceptDistribution(payload, blobB64, createdAt);
  // Confirm stored.
  const pre = await s.story(payload.storyId, createdAt);
  // Read past the boundary — should return null and purge.
  const post = await s.story(payload.storyId, createdAt + 25 * 3600);
  // After the purge, even a "fresh" read should not resurrect it.
  const again = await s.story(payload.storyId, createdAt);
  const ok = pre !== null && post === null && again === null;
  return ok
    ? pass(name)
    : fail(name, `pre=${pre !== null} post=${post !== null} again=${again !== null}`);
}

async function caseStorePurgeExpiredBulk(): Promise<TestCase> {
  const name = "D4: purgeExpired bulk: 3 expired + 2 active → 3 deleted, 2 retained";
  const s = freshStore();
  const anchor = 1_700_000_000;
  // 3 stories created 25h before anchor (expired at anchor time).
  const expiredIds = ["dead-1", "dead-2", "dead-3"];
  const aliveIds = ["alive-1", "alive-2"];
  for (const id of expiredIds) {
    const { payload, blobB64 } = makeAcceptInputs(id, anchor - 25 * 3600);
    await s.acceptDistribution(payload, blobB64, anchor - 25 * 3600);
  }
  for (const id of aliveIds) {
    const { payload, blobB64 } = makeAcceptInputs(id, anchor);
    await s.acceptDistribution(payload, blobB64, anchor);
  }
  // Sweep at anchor.
  await s.purgeExpired(anchor);
  const deadResults = await Promise.all(
    expiredIds.map((id) => s.story(id, anchor)),
  );
  const aliveResults = await Promise.all(
    aliveIds.map((id) => s.story(id, anchor)),
  );
  const allDeadGone = deadResults.every((r) => r === null);
  const allAlivePresent = aliveResults.every((r) => r !== null);
  const ok = allDeadGone && allAlivePresent;
  return ok
    ? pass(name)
    : fail(
        name,
        `dead=${deadResults.map((r) => r !== null)} alive=${aliveResults.map((r) => r !== null)}`,
      );
}

async function caseStoreReactionsLWW(): Promise<TestCase> {
  const name = "D5: reactions LWW by ts (stale ts is dropped)";
  const s = freshStore();
  const { payload, blobB64 } = sampleAcceptInputs();
  const createdAt = payload.metadata.createdAt;
  await s.acceptDistribution(payload, blobB64, createdAt);
  const r200: StoryReactionPayload = {
    schema: 1,
    storyId: payload.storyId,
    reactingUserId: "bob",
    emoji: "\u{1F600}",
    ts: 200,
  };
  const r100Stale: StoryReactionPayload = {
    schema: 1,
    storyId: payload.storyId,
    reactingUserId: "bob",
    emoji: "\u{1F622}",
    ts: 100,
  };
  const firstChanged = await s.recordReaction(r200, createdAt + 10);
  const secondChanged = await s.recordReaction(r100Stale, createdAt + 20);
  const stored = (await s.reactions(payload.storyId))[0];
  const ok =
    firstChanged === true &&
    secondChanged === false &&
    stored?.emoji === "\u{1F600}" &&
    stored?.ts === 200;
  return ok
    ? pass(name)
    : fail(name, `first=${firstChanged} second=${secondChanged} stored=${JSON.stringify(stored)}`);
}

async function caseStoreReactionsSorted(): Promise<TestCase> {
  const name = "D6: reactions sorted by reactingUserId";
  const s = freshStore();
  const { payload, blobB64 } = sampleAcceptInputs();
  const createdAt = payload.metadata.createdAt;
  await s.acceptDistribution(payload, blobB64, createdAt);
  const now = createdAt + 10;
  // Insert out of alphabetical order.
  await s.recordReaction(
    { schema: 1, storyId: payload.storyId, reactingUserId: "carol", emoji: "\u{1F525}", ts: 1 },
    now,
  );
  await s.recordReaction(
    { schema: 1, storyId: payload.storyId, reactingUserId: "alice", emoji: "\u{1F600}", ts: 2 },
    now,
  );
  await s.recordReaction(
    { schema: 1, storyId: payload.storyId, reactingUserId: "bob", emoji: "\u{1F44D}", ts: 3 },
    now,
  );
  const users = (await s.reactions(payload.storyId)).map((r) => r.reactingUserId);
  const ok = JSON.stringify(users) === JSON.stringify(["alice", "bob", "carol"]);
  return ok ? pass(name) : fail(name, `got ${JSON.stringify(users)}`);
}

// ---------------------------------------------------------------------------
// Group E: hardening
// ---------------------------------------------------------------------------

async function caseServerSubstituteAttack(): Promise<TestCase> {
  // Sender encrypts story-A; hostile server delivers the same blob under
  // metadata claiming it is story-B. AAD mismatch must reject.
  const name =
    "E1: server-substitute attack: same encryptedBlob + different metadata.storyId fails openFailure";
  const metaA = sampleMetadata("story-A");
  const metaB = sampleMetadata("story-B");
  try {
    const key = StoryKey.generate();
    const blob = new TextEncoder().encode("alice's selfie");
    const sealed = await StoryCipher.seal(blob, metaA, key);
    try {
      await StoryCipher.open(sealed, metaB, key, metaA.createdAt);
      return fail(name, "open succeeded under spoofed metadata");
    } catch (e) {
      if (isStoryErrorKind(e, "openFailure")) return pass(name);
      return fail(name, `expected openFailure, got ${String(e)}`);
    }
  } catch (e) {
    return fail(name, `seal failed: ${String(e)}`);
  }
}

async function caseStoreRevokeReaction(): Promise<TestCase> {
  const name = "E2: revoke (empty emoji) removes a prior reaction from the store";
  const s = freshStore();
  const { payload, blobB64 } = sampleAcceptInputs();
  const createdAt = payload.metadata.createdAt;
  await s.acceptDistribution(payload, blobB64, createdAt);
  const now = createdAt + 10;
  await s.recordReaction(
    {
      schema: 1,
      storyId: payload.storyId,
      reactingUserId: "bob",
      emoji: "\u{1F600}",
      ts: 100,
    },
    now,
  );
  const before = await s.reactions(payload.storyId);
  // Revoke with a fresher ts.
  await s.recordReaction(
    {
      schema: 1,
      storyId: payload.storyId,
      reactingUserId: "bob",
      emoji: "",
      ts: 200,
    },
    now,
  );
  const after = await s.reactions(payload.storyId);
  const ok = before.length === 1 && after.length === 0;
  return ok
    ? pass(name)
    : fail(name, `before=${before.length} after=${after.length}`);
}

async function casePerStoryIsolation(): Promise<TestCase> {
  const name = "E3: per-story isolation — two stories share a store but don't bleed";
  const s = freshStore();
  const anchor = 1_700_000_000;
  const { payload: p1, blobB64: b1 } = makeAcceptInputs("story-X", anchor);
  const { payload: p2, blobB64: b2 } = makeAcceptInputs("story-Y", anchor);
  await s.acceptDistribution(p1, b1, anchor);
  await s.acceptDistribution(p2, b2, anchor);
  // Add a reaction only to story-X.
  await s.recordReaction(
    {
      schema: 1,
      storyId: "story-X",
      reactingUserId: "alice",
      emoji: "\u{1F600}",
      ts: 1,
    },
    anchor + 1,
  );
  const rxX = await s.reactions("story-X");
  const rxY = await s.reactions("story-Y");
  // Purge story-X by reading past expiry; story-Y should survive.
  await s.story("story-X", anchor + 25 * 3600);
  const afterPurgeX = await s.story("story-X", anchor);
  const afterPurgeY = await s.story("story-Y", anchor);
  const ok =
    rxX.length === 1 &&
    rxY.length === 0 &&
    afterPurgeX === null &&
    afterPurgeY !== null;
  return ok
    ? pass(name)
    : fail(
        name,
        `rxX=${rxX.length} rxY=${rxY.length} afterX=${afterPurgeX !== null} afterY=${afterPurgeY !== null}`,
      );
}

// ---------------------------------------------------------------------------
// Fixtures
// ---------------------------------------------------------------------------

function sampleMetadata(storyId = "story-1"): StoryMetadata {
  return makeStoryMetadata(storyId, "alice", 5, "image/jpeg", 1_700_000_000);
}

function makeDistributionPayload(
  storyId: string,
  key: StoryKey,
  meta: StoryMetadata,
): StoryKeyDistributionPayload {
  return {
    schema: 1,
    storyId,
    // Standard base64 of the raw key bytes.
    storyKey: btoa(String.fromCharCode(...key.raw)),
    metadata: meta,
  };
}

function sampleAcceptInputs(): {
  payload: StoryKeyDistributionPayload;
  blobB64: string;
} {
  return makeAcceptInputs("story-1", 1_700_000_000);
}

function makeAcceptInputs(
  storyId: string,
  createdAt: number,
): { payload: StoryKeyDistributionPayload; blobB64: string } {
  const key = StoryKey.generate();
  const meta = makeStoryMetadata(storyId, "alice", 5, "image/jpeg", createdAt);
  // Provide a non-empty blob (the store doesn't decrypt at acceptance).
  const blobB64 = btoa("hello-" + storyId);
  const payload: StoryKeyDistributionPayload = {
    schema: 1,
    storyId,
    storyKey: btoa(String.fromCharCode(...key.raw)),
    metadata: meta,
  };
  return { payload, blobB64 };
}

function freshStore(): StoryStore {
  return new StoryStore(`kaether-selftest-story-${crypto.randomUUID()}`);
}

// ---------------------------------------------------------------------------
// Error helpers
// ---------------------------------------------------------------------------

function isStoryErrorKind(e: unknown, kind: string): boolean {
  return (
    typeof e === "object" &&
    e !== null &&
    "kind" in e &&
    (e as { kind: string }).kind === kind
  );
}

// Re-export STORY_TTL_SECONDS usage guard (keeps the import from being unused).
void STORY_TTL_SECONDS;
void tryDecodeStoryDistribution;
void isStoryReaction;
